import "./BulkImportModal.css";

import { FC, ReactNode, useCallback, useEffect, useState } from "react";

import { serverGetBulkImportSignedUrl, serverStartBulkImportJob } from "api/api-server-actions";
import { TermMenu } from "components/menus/TermMenu";
import { BulkImportType } from "components/server-types";
import { Trans, t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { Checkbox } from "mds/components/Checkbox";
import { FileUploadField } from "mds/components/FileUploadField";
import { IconText } from "mds/components/IconText";
import { LegacyButton } from "mds/components/LegacyButton";
import { Modal } from "mds/components/Modal";
import { TabBar } from "mds/components/TabBar";
import {
  PaperClipLinkIcon,
  PersonClassIcon,
  RefreshIcon,
  TextBooksIcon,
  TrashDeleteIcon,
} from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import {
  selectBulkImportJobById,
  selectCourses,
  selectCurrentOrg,
  selectTerms,
} from "store/selectors";
import { selectSectionsByTermAndCourse } from "store/selectors/section";
import { GENERAL_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { downloadCSVFile } from "utils/file";
import { BULK_IMPORT_GUIDE_URL } from "utils/urls";
import { fetchAndStoreUserInviteStatuses } from "utils/user-invite-status";

interface BulkImportModalProps {
  onClose: () => void;
}

const CSV_TEMPLATES: Record<BulkImportType, readonly string[]> = {
  users: [
    "External User ID",
    "Given Name",
    "Family Name",
    "Email",
    "Institution Role",
    "isAccountActive",
    "Password",
    "Should Send Invite",
  ],
  course_sections: [
    "Term",
    "External Section ID",
    "Forum Course Code",
    "Forum Course Title",
    "Optional Template Course Title",
  ],
  enrollments: ["Term", "External Section ID", "External User ID", "Role", "Was Course Dropped"],
} as const;

const IMPORT_TYPE_ICONS: Record<BulkImportType, React.ComponentType<{ className?: string }>> = {
  users: PersonClassIcon,
  course_sections: TextBooksIcon,
  enrollments: RefreshIcon,
} as const;

const uploadToS3 = async (file: File, uploadUrl: string): Promise<void> => {
  const response = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: {
      "Content-Type": "text/csv",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to upload file to S3: ${response.statusText}`);
  }
};

/**
 * Formats error messages from bulk import to display with bullet points as HTML
 */
const formatBulkImportError = (errorMessage: string): ReactNode => {
  const colonIdx = errorMessage.indexOf(": ");
  if (colonIdx !== -1) {
    const summary = errorMessage.slice(0, colonIdx + 1); // include the colon
    const messages = errorMessage.slice(colonIdx + 2); // skip colon and space
    const errors = messages
      .split(";")
      .map((e) => e.trim())
      .filter(Boolean);

    if (errors.length > 0) {
      return (
        <>
          {summary}
          <ul className="ml-5 list-disc">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </>
      );
    }
  }

  return errorMessage;
};

const ImportTabContent: FC<{ importType: BulkImportType; onClose: () => void }> = ({
  importType,
  onClose,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedTermId, setSelectedTermId] = useState<string>("");
  const [currentImportJobId, setCurrentImportJobId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<ReactNode | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const currentJob = useAppSelector((state) =>
    currentImportJobId ? selectBulkImportJobById(state, currentImportJobId) : null,
  );
  const courses = useAppSelector(selectCourses);
  const terms = useAppSelector(selectTerms);
  const org = useAppSelector(selectCurrentOrg);
  const orgName = org?.name;

  const [requireSystemCheck, setRequireSystemCheck] = useState(false);

  // Get sections for the selected term
  const sectionsForSelectedTerm = useAppSelector((s) =>
    selectSectionsByTermAndCourse(s, selectedTermId, ""),
  );

  // Hook to refresh invite statuses
  const dispatch = useAppDispatch();
  const refreshInviteStatuses = useCallback(async () => {
    await fetchAndStoreUserInviteStatuses(dispatch);
  }, [dispatch]);

  // Download CSV template using the utility function
  const downloadTemplate = useCallback(
    (type: BulkImportType) => {
      const headers: (string | number)[] = Array.from(CSV_TEMPLATES[type]);
      let rows: (string | number)[][] = [];

      // For course_sections, if a term is selected, include existing sections as rows
      if (type === "course_sections" && selectedTermId) {
        const selectedTerm = terms.find((term) => term.id === selectedTermId);
        if (selectedTerm) {
          rows = sectionsForSelectedTerm.map((section) => {
            const course = courses.find((cc) => cc.id === section.course_id);
            return [
              selectedTerm.title, // Term
              section.external_section_id || "", // External Section ID
              course?.title || "", // Forum Course Code
              section.title, // Forum Course Title
              "", // Optional Template Course Title
            ];
          });
        }
      }

      const csvData = {
        headers,
        rows,
      };

      downloadCSVFile(csvData, `${type}_template.csv`);
    },
    [selectedTermId, sectionsForSelectedTerm, courses, terms],
  );

  // Monitor job status changes via pubsub
  useEffect(() => {
    if (currentJob) {
      if (currentJob.status === "completed") {
        const recordsProcessed = currentJob.records_processed || 0;

        setSuccessMessage(
          t(`home.settings.bulk_import_modal.${importType}_import_success`, {
            recordsProcessed,
            orgName,
          }),
        );

        setCurrentImportJobId(null);
        setIsUploading(false);

        trackEvent("Bulk enrollment completed", GENERAL_EVENT_CATEGORY);

        // Refresh invite statuses only for user imports
        if (importType === "users") {
          refreshInviteStatuses();
        }
      } else if (currentJob.status === "error") {
        const jobErrorMessage = currentJob.error_message || t("error.toasts.unknown");
        const recordsProcessed = currentJob.records_processed || 0;

        // Check if this is a partial error (some records were processed)
        if (recordsProcessed > 0) {
          setSuccessMessage(
            t(`home.settings.bulk_import_modal.${importType}_import_success`, {
              recordsProcessed: currentJob.records_processed,
              orgName,
            }),
          );
        }

        setErrorMessage(formatBulkImportError(jobErrorMessage));
        setCurrentImportJobId(null);
        setIsUploading(false);
      }
    }
  }, [currentJob, importType, orgName, refreshInviteStatuses]);

  const handleFileDrop = (acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      setErrorMessage(null); // Clear any previous errors when a new file is selected
      setSuccessMessage(null); // Clear any previous success messages
    }
  };

  const handleImport = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setErrorMessage(null); // Clear any previous errors
    setSuccessMessage(null); // Clear any previous success messages

    try {
      // Step 1: Get pre-signed URL
      const { upload_url, s3_key } = await serverGetBulkImportSignedUrl(
        importType,
        selectedFile.name,
      );

      // Step 2: Upload file to S3
      await uploadToS3(selectedFile, upload_url);

      // Step 3: Start the import job and track the job ID
      const { import_job_id } = await serverStartBulkImportJob(
        importType,
        s3_key,
        selectedFile.name,
        importType === "users" ? requireSystemCheck : false,
      );
      setCurrentImportJobId(import_job_id);

      // Don't show success message here - wait for pubsub update
    } catch (error) {
      console.error(`Import ${importType} error:`, error);
      setErrorMessage(t("home.settings.bulk_import_modal.error_starting_import_job"));
      setIsUploading(false);
    }
  };

  const handleDeleteFile = () => {
    setSelectedFile(null);
    setErrorMessage(null);
    setSuccessMessage(null);
  };

  const handleReset = () => {
    setSelectedFile(null);
    setErrorMessage(null);
    setSuccessMessage(null);
    setCurrentImportJobId(null);
    setIsUploading(false);
  };

  const isProcessing = Boolean(isUploading || currentJob);
  const isSuccess = Boolean(successMessage && !errorMessage);

  return (
    <div className="flex flex-col gap-6">
      {importType === "course_sections" && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-black-tint-20">
            {t("home.settings.bulk_import_modal.term_label")}
          </span>

          <TermMenu
            defaultToAll={false}
            menuType="menu"
            selectedTermId={selectedTermId}
            showIcons={false}
            size="s"
            onTermChange={setSelectedTermId}
          />
        </div>
      )}

      <div className="text-sm text-black-tint-40">
        <Trans
          components={[
            <a
              aria-label="Download template"
              className="text-blue-shade-20 underline"
              href="#"
              key="template-link"
              onClick={(e) => {
                e.preventDefault();
                downloadTemplate(importType);
              }}
            />,
          ]}
          i18nKey={`home.settings.bulk_import_modal.import_descriptions.${importType}`}
          t={t}
        />
      </div>

      {importType === "users" && (
        <Checkbox
          checked={requireSystemCheck}
          className="mt-2 text-black-tint-40"
          id="require-system-check"
          onChange={(checked) => setRequireSystemCheck(checked)}
        >
          {t("home.settings.bulk_import_modal.require_system_check_option")}
        </Checkbox>
      )}

      {!isSuccess &&
        (selectedFile ? (
          <div className="flex w-full items-center justify-between rounded-sm bg-black-tint-97 py-2 pl-4 pr-2">
            <div className="flex items-center gap-2">
              <PaperClipLinkIcon className="text-black-tint-40" />
              <span className="font-semibold">{selectedFile.name}</span>
            </div>

            <LegacyButton
              disabled={isProcessing}
              kind="secondary"
              size="s"
              iconOnly
              onClick={handleDeleteFile}
            >
              <TrashDeleteIcon />
            </LegacyButton>
          </div>
        ) : (
          <FileUploadField
            accept="text/csv"
            isLoading={isProcessing}
            size="m"
            onDrop={handleFileDrop}
          />
        ))}

      {successMessage && !errorMessage && <Banner kind="success">{successMessage}</Banner>}

      {errorMessage && (
        <Banner kind="error">
          <div className="flex flex-col gap-2">
            <div>
              {t("home.settings.bulk_import_modal.import_failure_prefix")}
              {errorMessage}
            </div>

            {successMessage || t("home.settings.bulk_import_modal.import_failure_suffix")}
          </div>
        </Banner>
      )}

      <div className="mt-2 flex items-center justify-between">
        <a
          className="text-sm text-blue-shade-20 underline"
          href={BULK_IMPORT_GUIDE_URL}
          rel="noopener noreferrer"
          target="_blank"
        >
          {t("common.get_help")}
        </a>

        <div className="flex gap-2">
          {isSuccess ? (
            <>
              <LegacyButton kind="secondary" size="s" onClick={handleReset}>
                {t("home.settings.bulk_import_modal.upload_another_file")}
              </LegacyButton>

              <LegacyButton kind="primary" size="s" onClick={onClose}>
                {t("common.done")}
              </LegacyButton>
            </>
          ) : (
            <>
              <LegacyButton disabled={isProcessing} kind="secondary" size="s" onClick={onClose}>
                {t("common.cancel")}
              </LegacyButton>

              <LegacyButton
                disabled={!selectedFile || isProcessing}
                kind="primary"
                size="s"
                onClick={handleImport}
              >
                {isProcessing ? (
                  <IconText
                    iconStart={<RefreshIcon className="animate-spin" />}
                    text="Processing..."
                  />
                ) : (
                  t(`home.settings.bulk_import_modal.tabs.${importType}`)
                )}
              </LegacyButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const BulkImportModal: FC<BulkImportModalProps> = ({ onClose }) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  const tabs = Object.entries(IMPORT_TYPE_ICONS).map(
    ([importType, IconComponent]: [BulkImportType, FC<{ className?: string }>]) => {
      return {
        label: (
          <IconText
            iconStart={<IconComponent className="group-data-[selected]/tab:text-blue" />}
            text={t(`home.settings.bulk_import_modal.tabs.${importType}`)}
          />
        ),
        title: t(`home.settings.bulk_import_modal.tabs.${importType}`),
        panel: <ImportTabContent importType={importType} onClose={onClose} />,
      };
    },
  );

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />

      <Modal
        className="bulk-import-modal w-[598px]"
        title={t("home.settings.bulk_import_modal.title")}
        hideCancelConfirmButtons
        onClose={onClose}
      >
        <div className="modal-body">
          <TabBar selectedIndex={selectedTabIndex} tabs={tabs} onChange={setSelectedTabIndex} />
        </div>
      </Modal>
    </>
  );
};
